/**
 * Dynamic placeholder fill for question text.
 *
 * Several K-NAMHS questions reference items the respondent endorsed earlier via
 * `[TRAUMA EVENT]` / `[TRAUMA EVENT/S]` (PTSD) and `[SYMPTOMS ENDORSED]` (SoPh /
 * GAD / MDD — each referencing a different set of source items). The live
 * interviewer reads these back; this module reproduces that by splicing the
 * endorsed source items' short labels into the placeholder at render time.
 *
 * The short labels below are UNREVIEWED machine drafts (English authored from
 * each item; XiTsonga/Swahili machine-translated) — like the rest of the
 * translations they must be verified by a fluent speaker / against the K-NAMHS
 * manual before any non-prototype use. The XiTsonga drafts in particular are
 * rough.
 */
import type {Answer} from '../Engine/boolean';
import type {AppLanguageCode} from '../Utils/I18n';

// Source items whose "Yes" endorsement fills `[SYMPTOMS ENDORSED]`, per module.
const SYMPTOM_SOURCES: Record<string, string[]> = {
  y5soyima: ['ysoq001', 'ysoq002', 'ysoq003', 'ysoq004', 'ysoq005', 'ysoq006'],
  y5gayima: ['ygaq015', 'ygaq016', 'ygaq017', 'ygaq018', 'ygaq019', 'ygaq020', 'ygaq021'],
  y5mdyima: ['ymdq001', 'ymdq002', 'ymdq003'],
};

// Trauma types (PTSD criterion A) whose "Yes" fills `[TRAUMA EVENT(/S)]`.
const TRAUMA_SOURCES = [
  'yptq001', 'yptq002', 'yptq003', 'yptq004', 'yptq005', 'yptq006',
  'yptq007', 'yptq008', 'yptq009', 'yptq010', 'yptq011', 'yptq012',
];

const LABELS: Record<AppLanguageCode, Record<string, string>> = {
  en: {
    // PTSD trauma (noun phrases — read after "since" / "about" / "thought about")
    yptq001: 'the natural disaster',
    yptq002: 'the fire or explosion',
    yptq003: 'the bad accident',
    yptq004: 'the animal attack',
    yptq005: 'being attacked or beaten',
    yptq006: 'the unwanted sexual experience',
    yptq007: 'the dangerous situation',
    yptq008: 'seeing someone get hurt or killed',
    yptq009: 'seeing someone attacked',
    yptq010: 'seeing a dead body',
    yptq011: 'the death of someone close to you',
    yptq012: 'when someone close was in danger',
    // SoPh situations (verb phrases — read after "when you")
    ysoq001: "were around people you didn't know well",
    ysoq002: 'had to meet new people',
    ysoq003: 'were around a lot of people your age',
    ysoq004: 'were in social situations like a party or dance',
    ysoq005: 'had to do something in front of other people',
    ysoq006: 'had to do things while people were watching',
    // GAD symptoms (verb phrases — read after "you also")
    ygaq015: 'had tight or tense muscles',
    ygaq016: 'felt restless or keyed up',
    ygaq017: 'got tired easily',
    ygaq018: 'had trouble concentrating',
    ygaq019: 'had your mind go blank',
    ygaq020: 'had trouble sleeping',
    ygaq021: 'felt grouchy or irritable',
    // MDD mood (state phrases — read after "you've felt")
    ymdq001: 'sad or depressed',
    ymdq002: 'not interested in anything',
    ymdq003: 'grouchy or irritable',
  },
  ts: {
    yptq001: 'khombo ra ntumbuluko',
    yptq002: 'ndzilo kumbe ku pfupfa',
    yptq003: 'ngozi yo biha',
    yptq004: 'ku hlaseriwa hi xiharhi',
    yptq005: 'ku hlaseriwa kumbe ku biwa',
    yptq006: 'xiendlakalo xa masangu lexi u nga xi lavangiki',
    yptq007: 'xiyimo xa khombo',
    yptq008: 'ku vona munhu a vaviseka kumbe a dlawa',
    yptq009: 'ku vona munhu a hlaseriwa',
    yptq010: 'ku vona ntsumbu',
    yptq011: 'ku fa ka munhu loyi a tshinelaneke na wena',
    yptq012: 'loko munhu wa le kusuhi a ri ekhombyeni',
    ysoq001: 'u ri na vanhu lava u nga va tiviki kahle',
    ysoq002: 'u hlangana na vanhu lavantshwa',
    ysoq003: 'u ri na vanhu vo tala va ntangha wa wena',
    ysoq004: 'u ri eka minkhuvo yo fana ni phathi kumbe ku cina',
    ysoq005: 'u endla nchumu emahlweni ka vanhu',
    ysoq006: 'u endla swilo loko vanhu va ku languta',
    ygaq015: 'u ri ni mirhi yo tiya',
    ygaq016: 'u titwa u nga rhuli',
    ygaq017: 'u karhala hi ku hatlisa',
    ygaq018: 'u ri ni xiphiqo xo kongoma',
    ygaq019: 'miehleketo ya wena yi tshamela ku huma',
    ygaq020: 'u ri ni xiphiqo xo etlela',
    ygaq021: 'u hlundzukile kumbe u ri ni vukarhi',
    ymdq001: 'u tsanile kumbe u ri ni gome',
    ymdq002: 'u nga tsakisiwi hi nchumu',
    ymdq003: 'u hlundzukile kumbe u ri ni vukarhi',
  },
  sw: {
    yptq001: 'maafa ya asili',
    yptq002: 'moto au mlipuko',
    yptq003: 'ajali mbaya',
    yptq004: 'kushambuliwa na mnyama',
    yptq005: 'kushambuliwa au kupigwa',
    yptq006: 'tukio la ngono usilolitaka',
    yptq007: 'hali ya hatari',
    yptq008: 'kuona mtu akiumizwa au kuuawa',
    yptq009: 'kuona mtu akishambuliwa',
    yptq010: 'kuona maiti',
    yptq011: 'kifo cha mtu wa karibu nawe',
    yptq012: 'wakati mtu wa karibu alipokuwa hatarini',
    ysoq001: 'ulipokuwa karibu na watu usiowajua vizuri',
    ysoq002: 'ulipokutana na watu wapya',
    ysoq003: 'ulipokuwa na watu wengi wa rika lako',
    ysoq004: 'ulipokuwa katika hafla kama sherehe au dansi',
    ysoq005: 'ulipofanya jambo mbele ya watu',
    ysoq006: 'ulipofanya mambo huku watu wakikutazama',
    ygaq015: 'ulikuwa na misuli iliyokaza',
    ygaq016: 'ulihisi kutotulia',
    ygaq017: 'ulichoka haraka',
    ygaq018: 'ulipata shida kuzingatia',
    ygaq019: 'akili yako iliganda',
    ygaq020: 'ulipata shida kulala',
    ygaq021: 'ulikuwa mkali au mwenye hasira',
    ymdq001: 'huzuni au msongo',
    ymdq002: 'bila kupendezwa na chochote',
    ymdq003: 'mwenye hasira au kero',
  },
  fr: {
    // PTSD trauma (noun phrases — read after « depuis » / « à propos de »)
    yptq001: 'la catastrophe naturelle',
    yptq002: "l'incendie ou l'explosion",
    yptq003: 'le grave accident',
    yptq004: "l'attaque par un animal",
    yptq005: "le fait d'avoir été attaqué(e) ou battu(e)",
    yptq006: "l'expérience sexuelle non désirée",
    yptq007: 'la situation dangereuse',
    yptq008: "le fait d'avoir vu quelqu'un être blessé ou tué",
    yptq009: "le fait d'avoir vu quelqu'un se faire attaquer",
    yptq010: "le fait d'avoir vu un corps sans vie",
    yptq011: "le décès d'une personne proche de toi",
    yptq012: 'le moment où une personne proche était en danger',
    // SoPh situations (verb phrases — read after « quand tu »)
    ysoq001: 'étais avec des gens que tu ne connaissais pas bien',
    ysoq002: 'devais rencontrer de nouvelles personnes',
    ysoq003: 'étais avec beaucoup de jeunes de ton âge',
    ysoq004: 'étais dans des situations sociales comme une fête ou une soirée dansante',
    ysoq005: "devais faire quelque chose devant d'autres personnes",
    ysoq006: 'devais faire des choses pendant que des gens te regardaient',
    // GAD symptoms (verb phrases — read after « tu as aussi »)
    ygaq015: 'eu les muscles tendus ou crispés',
    ygaq016: 'été agité(e) ou à cran',
    ygaq017: 'été vite fatigué(e)',
    ygaq018: 'eu du mal à te concentrer',
    ygaq019: 'eu des passages à vide',
    ygaq020: 'eu du mal à dormir',
    ygaq021: 'été grognon(ne) ou irritable',
    // MDD mood (state phrases — read after « tu t'es senti(e) »)
    ymdq001: 'triste ou déprimé(e)',
    ymdq002: 'sans intérêt pour quoi que ce soit',
    ymdq003: 'grognon(ne) ou irritable',
  },
};

const AND: Record<AppLanguageCode, string> = {en: 'and', ts: 'na', sw: 'na', fr: 'et'};

const FALLBACK_TRAUMA: Record<AppLanguageCode, string> = {
  en: 'the difficult event you mentioned',
  ts: 'xiendlakalo xo nononʻhwa lexi u xi boxeke',
  sw: 'tukio gumu ulilolitaja',
  fr: "l'événement difficile que tu as mentionné",
};
const FALLBACK_SYMPTOM: Record<AppLanguageCode, string> = {
  en: 'the things you mentioned',
  ts: 'swilo leswi u swi boxeke',
  sw: 'mambo uliyoyataja',
  fr: 'les choses que tu as mentionnées',
};

const labelFor = (code: string, language: AppLanguageCode): string =>
  LABELS[language]?.[code] ?? LABELS.en[code] ?? code;

const joinPhrases = (items: string[], language: AppLanguageCode): string => {
  if (items.length <= 1) {
    return items[0] ?? '';
  }
  const head = items.slice(0, -1).join(', ');
  return `${head} ${AND[language]} ${items[items.length - 1]}`;
};

/**
 * Replace `[TRAUMA EVENT(/S)]` / `[SYMPTOMS ENDORSED]` in `title` with the
 * labels of the source items the respondent endorsed ("Yes") so far in the
 * active module. Falls back to a generic phrase when nothing is endorsed.
 */
export const fillPlaceholders = (
  title: string,
  moduleTarget: string | undefined,
  observed: ReadonlyMap<string, Answer> | undefined,
  language: AppLanguageCode,
): string => {
  if (!title || title.indexOf('[') < 0) {
    return title;
  }
  const obs = observed ?? new Map<string, Answer>();
  let out = title;

  if (out.indexOf('[TRAUMA EVENT') >= 0) {
    const endorsed = TRAUMA_SOURCES.filter(c => obs.get(c) === 'Yes').map(c => labelFor(c, language));
    const fill = endorsed.length ? joinPhrases(endorsed, language) : FALLBACK_TRAUMA[language] ?? FALLBACK_TRAUMA.en;
    out = out.replace(/\[TRAUMA EVENT\/S\]/g, fill).replace(/\[TRAUMA EVENT\]/g, fill);
  }

  if (out.indexOf('[SYMPTOMS ENDORSED]') >= 0) {
    const sources = (moduleTarget && SYMPTOM_SOURCES[moduleTarget]) || [];
    const endorsed = sources.filter(c => obs.get(c) === 'Yes').map(c => labelFor(c, language));
    const fill = endorsed.length ? joinPhrases(endorsed, language) : FALLBACK_SYMPTOM[language] ?? FALLBACK_SYMPTOM.en;
    out = out.replace(/\[SYMPTOMS ENDORSED\]/g, fill);
  }

  return out;
};
